import { useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  CircleAlert,
  CircleCheck,
  Eye,
  EyeOff,
  LoaderCircle,
  Lock,
  TriangleAlert,
} from "lucide-react";
import AuthFrame from "../components/PublicChrome/AuthFrame";
import AuthorizationService from "../../sdk/services/AuthService";
import {
  RESET_COPY,
  isExpiredResetToken,
  passwordsDisagree,
  resetFailureMessage,
} from "./redefinirSenhaState";
import "./RedefinirSenha.view.css";

type Phase = "form" | "success" | "expired";

export default function RedefinirSenhaView() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get("token") ?? "").trim();

  const [phase, setPhase] = useState<Phase>(token === "" ? "expired" : "form");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [mismatch, setMismatch] = useState(false);
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busyRef.current) return;

    if (passwordsDisagree(password, confirmation)) {
      setMismatch(true);
      setFormError("");
      return;
    }

    if (password === "") {
      setMismatch(false);
      return;
    }

    if (token === "") {
      setPhase("expired");
      return;
    }

    busyRef.current = true;
    setBusy(true);
    setFormError("");

    try {
      await AuthorizationService.resetPasswordByToken(token, password);
      setPhase("success");
    } catch (error: unknown) {
      if (isExpiredResetToken(error)) {
        setPhase("expired");
      } else {
        setFormError(resetFailureMessage(error));
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  if (phase === "expired") {
    return (
      <AuthFrame screen="redefinir-senha">
        <div className="reset-sheet">
          <div className="reset-sheet__seal reset-sheet__seal--bad" aria-hidden="true">
            <TriangleAlert size={30} aria-hidden="true" />
          </div>
          <div className="reset-sheet__outcome" role="alert">
            <h2 className="reset-sheet__outcome-title">{RESET_COPY.expiredTitle}</h2>
            <p className="reset-sheet__outcome-body">{RESET_COPY.expiredBody}</p>
          </div>
          <Link className="reset-sheet__cta" to="/esqueci-senha">
            {RESET_COPY.askNewLink}
          </Link>
          <p className="reset-sheet__switch reset-sheet__switch--after-cta">
            {RESET_COPY.remembered}{" "}
            <Link className="reset-sheet__switch-link" to="/login">
              {RESET_COPY.enter}
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </p>
        </div>
      </AuthFrame>
    );
  }

  if (phase === "success") {
    return (
      <AuthFrame screen="redefinir-senha">
        <div className="reset-sheet">
          <div className="reset-sheet__seal reset-sheet__seal--ok" aria-hidden="true">
            <CircleCheck size={30} aria-hidden="true" />
          </div>
          <div className="reset-sheet__outcome" role="status">
            <h2 className="reset-sheet__outcome-title">{RESET_COPY.successTitle}</h2>
            <p className="reset-sheet__outcome-body">{RESET_COPY.successBody}</p>
          </div>
          <Link className="reset-sheet__cta" to="/login">
            {RESET_COPY.enter}
            <ArrowRight size={20} aria-hidden="true" />
          </Link>
        </div>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame screen="redefinir-senha">
      <div className="reset-sheet">
        <h1 className="reset-sheet__title">{RESET_COPY.title}</h1>
        <p className="reset-sheet__subtitle">{RESET_COPY.subtitle}</p>
        <form className="reset-sheet__form" noValidate onSubmit={onSubmit} aria-busy={busy}>
          <div className="reset-sheet__field">
            <label className="reset-sheet__label" htmlFor="reset-password">
              {RESET_COPY.passwordLabel}
            </label>
            <div className="reset-sheet__control">
              <Lock className="reset-sheet__icon" size={20} aria-hidden="true" />
              <input
                id="reset-password"
                className="reset-sheet__input"
                type={showPassword ? "text" : "password"}
                name="password"
                autoComplete="new-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder={RESET_COPY.passwordPlaceholder}
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setMismatch(false);
                }}
                disabled={busy}
              />
              <button
                type="button"
                className="reset-sheet__reveal"
                aria-label={showPassword ? RESET_COPY.hidePassword : RESET_COPY.showPassword}
                aria-pressed={showPassword}
                disabled={busy}
                onClick={() => setShowPassword((current) => !current)}
              >
                {showPassword ? (
                  <EyeOff size={22} aria-hidden="true" />
                ) : (
                  <Eye size={22} aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
          <div className="reset-sheet__field">
            <label className="reset-sheet__label" htmlFor="reset-confirm">
              {RESET_COPY.confirmLabel}
            </label>
            <div
              className={
                mismatch
                  ? "reset-sheet__control reset-sheet__control--invalid"
                  : "reset-sheet__control"
              }
            >
              <Lock className="reset-sheet__icon" size={20} aria-hidden="true" />
              <input
                id="reset-confirm"
                className="reset-sheet__input"
                type={showConfirmation ? "text" : "password"}
                name="confirmation"
                autoComplete="new-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder={RESET_COPY.confirmPlaceholder}
                value={confirmation}
                onChange={(event) => {
                  setConfirmation(event.target.value);
                  setMismatch(false);
                }}
                disabled={busy}
                aria-invalid={mismatch}
                aria-describedby={mismatch ? "reset-confirm-error" : undefined}
              />
              <button
                type="button"
                className="reset-sheet__reveal"
                aria-label={
                  showConfirmation ? RESET_COPY.hidePassword : RESET_COPY.showPassword
                }
                aria-pressed={showConfirmation}
                disabled={busy}
                onClick={() => setShowConfirmation((current) => !current)}
              >
                {showConfirmation ? (
                  <EyeOff size={22} aria-hidden="true" />
                ) : (
                  <Eye size={22} aria-hidden="true" />
                )}
              </button>
            </div>
            {mismatch ? (
              <p id="reset-confirm-error" className="reset-sheet__error" role="alert">
                <CircleAlert size={16} aria-hidden="true" />
                <span>{RESET_COPY.unequal}</span>
              </p>
            ) : null}
          </div>
          {formError ? (
            <p className="reset-sheet__alert" role="alert">
              {formError}
            </p>
          ) : null}
          <button type="submit" className="reset-sheet__submit" disabled={busy} aria-busy={busy}>
            {busy ? (
              <>
                <LoaderCircle size={20} className="reset-sheet__spinner" aria-hidden="true" />
                {RESET_COPY.saving}
              </>
            ) : (
              RESET_COPY.submit
            )}
          </button>
          <div className="reset-sheet__rule" />
          <p className="reset-sheet__switch">
            {RESET_COPY.remembered}{" "}
            <Link className="reset-sheet__switch-link" to="/login">
              {RESET_COPY.enter}
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </p>
        </form>
      </div>
    </AuthFrame>
  );
}
